/**
 * Code Analysis Service
 *
 * Fetches code analysis artifacts from the ModernizeIT API.
 * Provides graph data, JSON artifacts, and generated code access.
 */

import { getAPIBaseUrl } from '../environment.js'

/**
 * Get the API base URL
 */
function getApiUrl() {
  return window.__MODERNIZEIT_API_URL__ || getAPIBaseUrl()
}

/**
 * Build auth headers from localStorage token
 */
function getAuthHeaders() {
  const token = localStorage.getItem('modernizeit_token')
  const headers = {}
  if (token) headers['Authorization'] = `Bearer ${token}`
  return headers
}

/**
 * Fetch a JSON artifact from code analysis results
 * @param {string} jobId - The code analysis job ID
 * @param {string} filename - The JSON artifact filename (e.g., 'cross_reference.json')
 * @returns {Promise<Object>} The parsed JSON content
 */
export async function getJsonArtifact(jobId, filename) {
  if (!jobId) {
    console.error(' [ANALYSIS_SERVICE] ERROR: jobId is missing!', { jobId, filename })
    throw new Error(`Missing jobId - cannot fetch ${filename}`)
  }

  const url = `${getApiUrl()}/codeanalysis/${jobId}/results/json/${filename}`
  console.log(' [ANALYSIS_SERVICE] Fetching JSON:', { jobId, filename, url })

  const response = await fetch(url, { headers: getAuthHeaders() })
  if (!response.ok) {
    // Use debug level — callers decide whether the artifact is required or optional.
    // AnalyzeView uses Promise.allSettled so a 404 here is non-fatal for optional files.
    console.debug(' [ANALYSIS_SERVICE] Artifact not found:', { jobId, filename, status: response.status })
    throw new Error(`Failed to fetch ${filename}: ${response.status}`)
  }

  return response.json()
}

/**
 * Fetch a graph file (DOT or PNG) from code analysis results
 * @param {string} jobId - The code analysis job ID
 * @param {string} filename - The graph filename (e.g., 'ifpr321_high_level.dot')
 * @param {string} format - 'dot' for text, 'png' for image
 * @returns {Promise<string|Blob>} The file content
 */
export async function getGraphArtifact(jobId, filename, format = 'dot') {
  if (!jobId) {
    console.error(' [ANALYSIS_SERVICE] ERROR: jobId is missing!', { jobId, filename, format })
    throw new Error(`Missing jobId - cannot fetch graph ${filename}`)
  }

  const url = `${getApiUrl()}/codeanalysis/${jobId}/results/graphs/${filename}`
  console.log(' [ANALYSIS_SERVICE] Fetching graph:', { jobId, filename, format, url })

  const response = await fetch(url, { headers: getAuthHeaders() })
  if (!response.ok) {
    console.error(' [ANALYSIS_SERVICE] Fetch failed:', { jobId, filename, status: response.status, url })
    throw new Error(`Failed to fetch graph ${filename}: ${response.status} - ${url}`)
  }

  if (format === 'png') {
    return response.blob()
  }

  return response.text()
}

/**
 * Get all available artifacts for a job
 * @param {string} jobId - The code analysis job ID
 * @returns {Promise<Object>} The job results with artifact lists
 */
export async function getJobResults(jobId) {
  const url = `${getApiUrl()}/codeanalysis/${jobId}/results`

  const response = await fetch(url, { headers: getAuthHeaders() })
  if (!response.ok) {
    throw new Error(`Failed to fetch job results: ${response.status}`)
  }

  return response.json()
}

/**
 * Extract base_name from job artifacts
 * Looks for files like {basename}_procedure_model.json or {basename}_summary.json
 * @param {string} jobId - The code analysis job ID
 * @returns {Promise<string|null>} The base name or null if not found
 */
export async function getJobBaseName(jobId) {
  try {
    const results = await getJobResults(jobId)

    // Prefer the authoritative base_name returned directly from output_data
    if (results.base_name) return results.base_name

    // Fallback: infer from artifact filename patterns
    const jsonArtifacts = results.json_artifacts || []
    for (const filename of jsonArtifacts) {
      const match = filename.match(/^(.+?)_(procedure_model|summary|data_model|line_inventory)\.json$/)
      if (match) return match[1]
    }

    return null
  } catch (err) {
    console.debug('Could not get job base name:', err)
    return null
  }
}

/**
 * Get the procedure model for paragraph/section structure
 * @param {string} jobId - The code analysis job ID
 * @param {string} baseName - The program base name (e.g., 'ifpr321')
 * @returns {Promise<Object>} The procedure model
 */
export async function getProcedureModel(jobId, baseName) {
  return getJsonArtifact(jobId, `${baseName}_procedure_model.json`)
}

/**
 * Get the cross reference data
 * @param {string} jobId - The code analysis job ID
 * @returns {Promise<Object>} The cross reference data
 */
export async function getCrossReference(jobId) {
  console.log(' [ANALYSIS_SERVICE] Getting cross_reference from comprehensive_parse_results')
  // Worker-mode: cross_reference is embedded in comprehensive_parse_results.json
  const comprehensive = await getJsonArtifact(jobId, 'comprehensive_parse_results.json')
  return comprehensive.cross_reference || {}
}

/**
 * Get the summary data
 * @param {string} jobId - The code analysis job ID
 * @param {string} baseName - The program base name (e.g., 'ifpr321')
 * @returns {Promise<Object>} The summary data
 */
export async function getSummary(jobId, baseName) {
  console.log(' [ANALYSIS_SERVICE] Getting summary from comprehensive_parse_results')
  // Worker-mode: summary is embedded in comprehensive_parse_results.json
  const comprehensive = await getJsonArtifact(jobId, 'comprehensive_parse_results.json')
  return comprehensive.summary || {}
}

/**
 * Get the data model
 * @param {string} jobId - The code analysis job ID
 * @param {string} baseName - The program base name (e.g., 'ifpr321')
 * @returns {Promise<Object>} The data model
 */
export async function getDataModel(jobId, baseName) {
  return getJsonArtifact(jobId, `${baseName}_complete_data_model.json`)
}

/**
 * Get a DOT graph file and parse it
 * @param {string} jobId - The code analysis job ID
 * @param {string} baseName - The program base name (e.g., 'ifpr321')
 * @param {string} graphType - 'high_level', 'summary', or 'detailed'
 * @returns {Promise<string>} The DOT file content
 */
export async function getGraphDot(jobId, baseName, graphType) {
  console.warn(' [ANALYSIS_SERVICE] Graph files (.dot) are not produced by worker-mode')
  // Worker-mode does NOT produce .dot graph files
  // Return null so UI can show "Graph not available" instead of error
  return null
}

/**
 * Fetch all graph data for a job
 * This is a convenience method that fetches all three graph types
 * @param {string} jobId - The code analysis job ID
 * @param {string} baseName - The program base name
 * @returns {Promise<Object>} Object with high_level, summary, detailed DOT content
 */
export async function getAllGraphs(jobId, baseName) {
  const [highLevel, summary, detailed] = await Promise.allSettled([
    getGraphDot(jobId, baseName, 'high_level'),
    getGraphDot(jobId, baseName, 'summary'),
    getGraphDot(jobId, baseName, 'detailed'),
  ])

  return {
    high_level: highLevel.status === 'fulfilled' ? highLevel.value : null,
    summary: summary.status === 'fulfilled' ? summary.value : null,
    detailed: detailed.status === 'fulfilled' ? detailed.value : null,
  }
}

/**
 * Fetch comprehensive data for the dependency graph view
 * @param {string} jobId - The code analysis job ID
 * @param {string} baseName - The program base name
 * @returns {Promise<Object>} All data needed for graph visualization
 */
export async function getGraphViewData(jobId, baseName) {
  const [graphs, crossRef, procedureModel, summary] = await Promise.allSettled([
    getAllGraphs(jobId, baseName),
    getCrossReference(jobId),
    getProcedureModel(jobId, baseName),
    getSummary(jobId, baseName),
  ])

  return {
    graphs: graphs.status === 'fulfilled' ? graphs.value : {},
    crossReference: crossRef.status === 'fulfilled' ? crossRef.value : null,
    procedureModel: procedureModel.status === 'fulfilled' ? procedureModel.value : null,
    summary: summary.status === 'fulfilled' ? summary.value : null,
    error: [graphs, crossRef, procedureModel, summary].find(r => r.status === 'rejected')?.reason?.message,
  }
}

/**
 * Fetch dependency mapper results
 * @param {string} jobId - The dependency mapper job ID
 * @returns {Promise<Object>} The dependency mapper results
 */
export async function getDependencyMapperResults(jobId) {
  const url = `${getApiUrl()}/dependencymapper/${jobId}/results`

  const response = await fetch(url, { headers: getAuthHeaders() })
  if (!response.ok) {
    throw new Error(`Failed to fetch dependency mapper results: ${response.status}`)
  }

  return response.json()
}

/**
 * Fetch a specific dependency mapper artifact
 * @param {string} jobId - The dependency mapper job ID
 * @param {string} artifactName - The artifact name (e.g., 'dependency_graph', 'coupling_metrics')
 * @returns {Promise<Object>} The artifact content
 */
export async function getDependencyMapperArtifact(jobId, artifactName) {
  // API endpoint is /dependencymapper/{job_id}/results/json/{filename}.json
  const filename = artifactName.endsWith('.json') ? artifactName : `${artifactName}.json`
  const url = `${getApiUrl()}/dependencymapper/${jobId}/results/json/${filename}`

  const response = await fetch(url, { headers: getAuthHeaders() })
  if (!response.ok) {
    throw new Error(`Failed to fetch dependency mapper artifact ${artifactName}: ${response.status}`)
  }

  return response.json()
}

/**
 * Fetch all dependency mapper data for visualization
 * @param {string} jobId - The dependency mapper job ID
 * @returns {Promise<Object>} All dependency mapper data
 */
export async function getDependencyMapperData(jobId) {
  // Try to get full results first
  const results = await getDependencyMapperResults(jobId)

  // The results should contain the artifacts or paths to them
  return {
    dependencyGraph: results.dependency_graph || results.artifacts?.dependency_graph,
    couplingMetrics: results.coupling_metrics || results.artifacts?.coupling_metrics,
    riskAssessment: results.risk_assessment || results.artifacts?.risk_assessment,
    microserviceBoundaries: results.microservice_boundaries || results.artifacts?.microservice_boundaries,
    impactAnalysis: results.impact_analysis || results.artifacts?.impact_analysis,
    staticAnalysis: results.static_analysis || results.artifacts?.static_analysis,
    summary: results.summary,
  }
}

/**
 * ============================================
 * MONOLITH IDENTIFIER API
 * ============================================
 */

/**
 * Fetch monolith identifier results
 * @param {string} jobId - The monolith identifier job ID
 * @returns {Promise<Object>} The monolith identifier results
 */
export async function getMonolithIdentifierResults(jobId) {
  const url = `${getApiUrl()}/monolithidentifier/${jobId}/results`

  const response = await fetch(url, { headers: getAuthHeaders() })
  if (!response.ok) {
    throw new Error(`Failed to fetch monolith identifier results: ${response.status}`)
  }

  return response.json()
}

/**
 * Fetch a specific monolith identifier artifact
 * @param {string} jobId - The monolith identifier job ID
 * @param {string} artifactName - The artifact name (e.g., 'decomposition_strategy', 'business_capabilities')
 * @returns {Promise<Object>} The artifact content
 */
export async function getMonolithIdentifierArtifact(jobId, artifactName) {
  // API endpoint is /monolithidentifier/{job_id}/results/json/{filename}.json
  const filename = artifactName.endsWith('.json') ? artifactName : `${artifactName}.json`
  const url = `${getApiUrl()}/monolithidentifier/${jobId}/results/json/${filename}`

  const response = await fetch(url, { headers: getAuthHeaders() })
  if (!response.ok) {
    throw new Error(`Failed to fetch monolith identifier artifact ${artifactName}: ${response.status}`)
  }

  return response.json()
}

/**
 * ============================================
 * DATA ANALYSIS API
 * ============================================
 */

/**
 * Fetch data analysis results
 * @param {string} jobId - The data analysis job ID
 * @returns {Promise<Object>} The data analysis results
 */
export async function getDataAnalysisResults(jobId) {
  const url = `${getApiUrl()}/dataanalysis/${jobId}/results`

  const response = await fetch(url, { headers: getAuthHeaders() })
  if (!response.ok) {
    throw new Error(`Failed to fetch data analysis results: ${response.status}`)
  }

  return response.json()
}

/**
 * Fetch a specific data analysis artifact
 * @param {string} jobId - The data analysis job ID
 * @param {string} filename - The artifact filename (e.g., 'erd.json')
 * @returns {Promise<Object>} The artifact content
 */
export async function getDataAnalysisArtifact(jobId, filename) {
  const url = `${getApiUrl()}/dataanalysis/${jobId}/results/json/${filename}`

  const response = await fetch(url, { headers: getAuthHeaders() })
  if (!response.ok) {
    throw new Error(`Failed to fetch data analysis artifact ${filename}: ${response.status}`)
  }

  return response.json()
}

/**
 * Normalize DA lineage flows to the { from, to, type } shape DataLineageChart expects.
 *
 * DA produces: { program, table, operation }
 *   READ  → data flows FROM the file INTO the program
 *   WRITE → data flows FROM the program OUT to the file
 *   CALL  → data flows FROM caller TO callee (not in DA, comes from CA fallback)
 */
function _normalizeLineageFlows(raw) {
  if (!raw?.flows?.length) return null
  const normalized = raw.flows
    .map(f => {
      const op = (f.operation || '').toUpperCase()
      const prog = f.program || ''
      const table = f.table || f.cobol_entity || ''
      if (!prog || !table) return null
      if (op === 'READ') {
        return { from: table, to: prog, type: 'FILE_READ', label: op }
      }
      if (op === 'WRITE') {
        return { from: prog, to: table, type: 'FILE_WRITE', label: op }
      }
      if (op === 'UPDATE' || op === 'DELETE') {
        return { from: prog, to: table, type: 'FILE_IO', label: op }
      }
      return null
    })
    .filter(Boolean)

  return normalized.length ? { flows: normalized } : null
}

/**
 * Adapt copybook_catalog.json items to the { name, used_by, fields } shape
 * expected by the CopybookAnalysis component.
 */
function _adaptCopybookCatalog(raw) {
  if (!raw?.copybooks?.length) return null
  return {
    copybooks: raw.copybooks.map(cb => ({
      name: cb.name || cb.source_file || 'UNKNOWN',
      used_by: [],                        // catalog has count only, not list
      shared_by: cb.shared_by_programs || 0,
      fields: Array.from({ length: cb.field_count || 0 }, (_, i) => ({ name: `Field ${i + 1}`, type: '' })),
      field_count: cb.field_count || 0,
      classification: cb.classification || '',
    })),
  }
}

/**
 * Fetch all data analysis data for visualization.
 *
 * Actual S3 artifacts produced by the DA engine:
 *   erd.json              — entities + relationships
 *   data_lineage.json     — { flows: [{ program, table, operation }] }
 *   copybook_catalog.json — { copybooks: [{ name, field_count, shared_by_programs }] }
 *   (no data_structures artifact — omitted)
 *
 * @param {string} jobId - The data analysis job ID
 * @returns {Promise<Object>} Normalized data analysis artifacts
 */
export async function getDataAnalysisData(jobId) {
  const [erd, lineage, copybooks] = await Promise.allSettled([
    getDataAnalysisArtifact(jobId, 'erd.json'),
    getDataAnalysisArtifact(jobId, 'data_lineage.json'),
    getDataAnalysisArtifact(jobId, 'copybook_catalog.json'),
  ])

  return {
    erd:              erd.status === 'fulfilled' ? erd.value : null,
    dataLineage:      lineage.status === 'fulfilled' ? _normalizeLineageFlows(lineage.value) : null,
    copybookAnalysis: copybooks.status === 'fulfilled' ? _adaptCopybookCatalog(copybooks.value) : null,
    dataStructures:   null,   // no DA artifact for this — CA fallback handles it
  }
}

/**
 * ============================================
 * DISCOVERY API
 * ============================================
 */

/**
 * Fetch discovery results
 * @param {string} jobId - The discovery job ID
 * @returns {Promise<Object>} The discovery results
 */
export async function getDiscoveryResults(jobId) {
  // API endpoint is /discovery/results/{job_id}
  const url = `${getApiUrl()}/discovery/results/${jobId}`

  const response = await fetch(url, { headers: getAuthHeaders() })
  if (!response.ok) {
    throw new Error(`Failed to fetch discovery results: ${response.status}`)
  }

  return response.json()
}

/**
 * Fetch a specific discovery artifact
 * @param {string} jobId - The discovery job ID
 * @param {string} artifactName - The artifact name
 * @returns {Promise<Object>} The artifact content
 */
export async function getDiscoveryArtifact(jobId, artifactName) {
  const url = `${getApiUrl()}/discovery/${jobId}/results/${artifactName}`

  const response = await fetch(url, { headers: getAuthHeaders() })
  if (!response.ok) {
    throw new Error(`Failed to fetch discovery artifact ${artifactName}: ${response.status}`)
  }

  return response.json()
}

/**
 * ============================================
 * ARCHITECTURE API
 * ============================================
 */

/**
 * Fetch architecture results
 * @param {string} jobId - The architecture job ID
 * @returns {Promise<Object>} The architecture results
 */
export async function getArchitectureResults(jobId) {
  const url = `${getApiUrl()}/architecture/${jobId}/results`

  const response = await fetch(url, { headers: getAuthHeaders() })
  if (!response.ok) {
    throw new Error(`Failed to fetch architecture results: ${response.status}`)
  }

  return response.json()
}

/**
 * Fetch a specific architecture artifact
 * @param {string} jobId - The architecture job ID
 * @param {string} artifactName - The artifact name
 * @returns {Promise<Object>} The artifact content
 */
export async function getArchitectureArtifact(jobId, artifactName) {
  const url = `${getApiUrl()}/architecture/${jobId}/results/${artifactName}`

  const response = await fetch(url, { headers: getAuthHeaders() })
  if (!response.ok) {
    throw new Error(`Failed to fetch architecture artifact ${artifactName}: ${response.status}`)
  }

  return response.json()
}

/**
 * ============================================
 * TIER 1 ENHANCEMENT ANALYSIS API
 * ============================================
 * Graph analysis queries served as JSON artifacts via the codeanalysis job route.
 * Each returns { query_name, description, records, record_count }.
 */

/**
 * Fetch dead code detection results
 * @param {string} jobId - The code analysis job ID
 * @returns {Promise<Object>} Paragraphs with no incoming PERFORMS edges
 */
export async function getDeadCodeAnalysis(jobId) {
  return getJsonArtifact(jobId, 'dead_code_analysis.json')
}

/**
 * Fetch copybook change impact analysis
 * @param {string} jobId - The code analysis job ID
 * @returns {Promise<Object>} Copybook blast radius — programs and paragraphs affected
 */
export async function getCopybookChangeImpact(jobId) {
  return getJsonArtifact(jobId, 'copybook_change_impact.json')
}

/**
 * Fetch field cross-reference data
 * @param {string} jobId - The code analysis job ID
 * @returns {Promise<Object>} Every field with PIC type, copybook, and usage
 */
export async function getFieldCrossReference(jobId) {
  return getJsonArtifact(jobId, 'field_cross_reference.json')
}

/**
 * Fetch complexity heatmap data
 * @param {string} jobId - The code analysis job ID
 * @returns {Promise<Object>} Paragraphs ranked by complexity score
 */
export async function getComplexityHeatmap(jobId) {
  return getJsonArtifact(jobId, 'complexity_heatmap.json')
}


export default {
  getJsonArtifact,
  getGraphArtifact,
  getJobResults,
  getJobBaseName,
  getProcedureModel,
  getCrossReference,
  getSummary,
  getDataModel,
  getGraphDot,
  getAllGraphs,
  getGraphViewData,
  getDependencyMapperResults,
  getDependencyMapperArtifact,
  getDependencyMapperData,
  // Monolith Identifier
  getMonolithIdentifierResults,
  getMonolithIdentifierArtifact,
  // Data Analysis
  getDataAnalysisResults,
  getDataAnalysisArtifact,
  getDataAnalysisData,
  // Discovery
  getDiscoveryResults,
  getDiscoveryArtifact,
  // Architecture
  getArchitectureResults,
  getArchitectureArtifact,
  // Tier 1 Enhancements
  getDeadCodeAnalysis,
  getCopybookChangeImpact,
  getFieldCrossReference,
  getComplexityHeatmap,
}
